import { Component } from "react";

const RELOAD_GUARD_KEY = "workivity-chunk-reload-at";
/** Only auto-reload once per minute, so a genuine failure cannot loop. */
const RELOAD_GUARD_MS = 60 * 1000;

/**
 * A deploy replaces the hashed bundles, so a tab that was already open asks for
 * a chunk that no longer exists. React treats that failed import as a render
 * error, and without a boundary the whole tree unmounts and the page goes
 * blank. This catches it and reloads into the new build instead.
 */
function isChunkLoadError(error) {
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \S+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

function readLastReloadAt() {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch (_error) {
    return 0;
  }
}

function markReloaded() {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (_error) {
    /* a blocked sessionStorage only costs us the loop guard */
  }
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) {
      console.error("[app] Unhandled render error:", error);
      return;
    }

    // Stale bundle after a deploy: fetch the new one, unless we just tried.
    if (Date.now() - readLastReloadAt() > RELOAD_GUARD_MS) {
      markReloaded();
      this.setState({ reloading: true });
      window.location.reload();
    }
  }

  handleReload() {
    markReloaded();
    window.location.reload();
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) {
      return this.props.children;
    }

    const stale = isChunkLoadError(error);
    return (
      // Styles are inline because the stylesheet may be part of what failed.
      <main
        role="alert"
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: "24px",
          fontFamily: "system-ui, -apple-system, Segoe UI, sans-serif",
          background: "#f4f6fb",
          color: "#3d455c",
        }}
      >
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          <h1 style={{ fontSize: "1.1rem", margin: "0 0 8px" }}>
            {stale ? "A new version is available" : "Something went wrong"}
          </h1>
          <p style={{ fontSize: "0.86rem", lineHeight: 1.6, color: "#6b7488", margin: "0 0 18px" }}>
            {stale
              ? "This tab was open while the app was updated. Reload to load the new version."
              : "The page could not be displayed. Reloading usually fixes it."}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            disabled={reloading}
            style={{
              border: 0,
              borderRadius: 8,
              padding: "10px 22px",
              background: "#6744dc",
              color: "#ffffff",
              fontSize: "0.86rem",
              fontWeight: 600,
              cursor: reloading ? "default" : "pointer",
              opacity: reloading ? 0.7 : 1,
            }}
          >
            {reloading ? "Reloading…" : "Reload"}
          </button>
          {stale ? null : (
            <pre
              style={{
                marginTop: 18,
                padding: 12,
                background: "#ffffff",
                border: "1px solid #e4e9f3",
                borderRadius: 8,
                fontSize: "0.72rem",
                color: "#8a91a5",
                textAlign: "left",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {String(error?.message || error)}
            </pre>
          )}
        </div>
      </main>
    );
  }
}

export default ErrorBoundary;
